import { useEffect, useState, type ReactNode } from "react";
import { DiscordMark } from "../components/DiscordMark";

interface ShotFrameProps {
  /** Screenshot path under /public. If it loads, the real image is shown; if
   *  it is absent (404) or fails, the built-in `fallback` preview is shown —
   *  so dropping the PNG in "just works" and a real screenshot always wins. */
  src?: string;
  /** Caption shown under the bezel. */
  label: string;
  /** CSS aspect-ratio for the fallback screen, e.g. "16 / 9". Defaults to 16/9.
   *  Only applies to the abstract fallback — a real screenshot is shown at its
   *  own natural aspect inside a clean (title-bar-less) panel. */
  aspect?: string;
  /** Self-contained, on-brand content rendered inside the screen when there
   *  is no real image. Fills the 16:9 screen. */
  fallback: ReactNode;
}

/**
 * A screenshot framed in a Discord-window / CRT bezel: rounded shell, traffic-
 * light dots, neon glow and a scanline overlay on the screen.
 *
 * Auto-swap: the `src` is probed with an off-DOM `Image()` preload. While it is
 * absent or fails to load (e.g. a 404 because the PNG hasn't been dropped in
 * yet) the supplied `fallback` preview fills the screen; once the probe
 * succeeds the real screenshot replaces it. Probing instead of an inline
 * `onError` keeps the swap deterministic regardless of lazy-loading / viewport,
 * so dropping `hero.png` / `gameplay.png` into /public/landing overrides the
 * built-in preview with zero code changes.
 */
export function ShotFrame({ src, label, aspect = "16 / 9", fallback }: ShotFrameProps) {
  // `null` until the probe decodes the real shot; then it holds the image's
  // natural [w, h] so the screen frame can take the shot's own aspect ratio
  // (full-bleed, no letterbox, no squish) regardless of whether it's a wide
  // 16:9 capture or a near-square one.
  const [natural, setNatural] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!src) {
      setNatural(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      // Guard against a server returning an HTML error page as a "successful"
      // image (0×0) — only treat genuinely-decoded pixels as a real shot.
      if (!cancelled && img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNatural([img.naturalWidth, img.naturalHeight]);
      }
    };
    img.onerror = () => {
      if (!cancelled) setNatural(null);
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src]);

  const showImage = Boolean(src) && natural !== null;

  // The real screenshot already includes the app's own dark header chrome
  // ("Playing as … on Game Boy Advance" + Change console / Load another ROM),
  // so stacking the fake Discord window title bar on top of it reads as a
  // double frame. For the real-image case we drop the title bar and present a
  // clean rounded panel that fits the shot at its natural aspect (object-fit:
  // contain) so the full header + game is visible without distortion or
  // over-crop. The abstract fallback keeps the full bezel + 16:9 screen below.
  if (showImage && natural) {
    const [w, h] = natural;
    return (
      <figure className="lp-bezel lp-bezel--shot m-0 w-full">
        {/* The screen takes the shot's own aspect ratio so the image is
            full-bleed: no letterbox bars, no distortion, no over-crop. */}
        <div className="lp-screen lp-screen--shot" style={{ aspectRatio: `${w} / ${h}` }}>
          <img src={src} alt={label} className="lp-shot-img" decoding="async" />
        </div>

        <figcaption
          className="lp-term px-3 pb-2 pt-3 text-center"
          style={{ color: "var(--crt-ink-dim)", fontSize: "1.15rem" }}
        >
          {label}
        </figcaption>
      </figure>
    );
  }

  return (
    <figure className="lp-bezel m-0 w-full">
      <div className="lp-bezel-bar">
        <span className="lp-dot" style={{ background: "#ff5f57" }} />
        <span className="lp-dot" style={{ background: "#febc2e" }} />
        <span className="lp-dot" style={{ background: "#28c840" }} />
        <DiscordMark
          size={14}
          className="ml-2 shrink-0"
          style={{ color: "rgba(255,255,255,0.55)" }}
        />
        <span
          className="lp-term truncate"
          style={{ color: "var(--crt-ink-muted)", fontSize: "1rem" }}
        >
          retro.discordsays.com
        </span>
      </div>

      <div className="lp-screen" style={{ aspectRatio: aspect }}>
        {fallback}
      </div>

      <figcaption
        className="lp-term px-3 pb-2 pt-3 text-center"
        style={{ color: "var(--crt-ink-dim)", fontSize: "1.15rem" }}
      >
        {label}
      </figcaption>
    </figure>
  );
}
